import { cn } from '@portals-platform/ui';

/**
 * The single definition of what a form control looks like in this design language.
 *
 * Every field — the hand-rolled ones and the ones wrapping a shadcn primitive — renders
 * through this, so a combobox trigger and a text input are the same height, radius and
 * focus treatment. Previously text/select/textarea each carried their own copy of this
 * class list and had already drifted.
 */
// `| undefined` on every member is deliberate: exactOptionalPropertyTypes is on repo-wide,
// and callers forward a `className?: string` straight through from their own props.
export interface ControlClassOptions {
  hasError?: boolean | undefined;
  /** Leaves room for a leading icon. */
  hasPrefixIcon?: boolean | undefined;
  /** Leaves room for a leading text affix (currency code, protocol, …). */
  hasPrefix?: boolean | undefined;
  /** Leaves room for a trailing affix or button. */
  hasSuffix?: boolean | undefined;
  className?: string | undefined;
}

export const CONTROL_BASE =
  'flex h-10 w-full rounded-[var(--radius-md)] border border-input bg-muted/40 px-3 text-[15px] text-foreground placeholder:text-muted-foreground/60 outline-none transition-[border-color,background,box-shadow] duration-150';

export const CONTROL_INTERACTION =
  'hover:bg-muted/60 focus:bg-background focus:border-primary focus:ring-3 focus:ring-primary/15 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground';

export const CONTROL_ERROR =
  'border-destructive bg-destructive/5 focus:border-destructive focus:ring-destructive/15';

export function controlClasses({
  hasError,
  hasPrefixIcon,
  hasPrefix,
  hasSuffix,
  className,
}: ControlClassOptions = {}): string {
  return cn(
    CONTROL_BASE,
    CONTROL_INTERACTION,
    hasError && CONTROL_ERROR,
    hasPrefixIcon && 'pl-9',
    hasPrefix && 'pl-12',
    hasSuffix && 'pr-10',
    className,
  );
}

/** Trigger styling for popover-backed fields (date, combobox, multi-select). */
export function triggerClasses(options: ControlClassOptions = {}): string {
  return cn(
    controlClasses(options),
    'items-center justify-between gap-2 text-left font-normal',
    'data-[placeholder]:text-muted-foreground/60',
  );
}
